import { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import { allowsManualStageChange, PIPELINE_COLUMNS, NEXT_STAGE } from "./pipelineColumns";
import PipelineColumn from "./PipelineColumn";
import { PipelinePickup } from "./types";
import { api } from "services/api.service";
import { useAlert } from "hooks/useAlert";

type Props = {
  filteredPickups: PipelinePickup[];
  onAdvanced?: () => void;
};

export default function LogisticsPickupPipelineView({ filteredPickups, onAdvanced }: Props) {
  const [advancingUuid, setAdvancingUuid] = useState<string | null>(null);
  const { alertSuccess, alertError } = useAlert();

  const displayedPickups = filteredPickups;

  const pickupsByStage = PIPELINE_COLUMNS.reduce(
    (acc, column) => {
      acc[column.stage] = displayedPickups.filter((p) => p.stage === column.stage);
      return acc;
    },
    {} as Record<string, PipelinePickup[]>,
  );

  const advancePickup = useCallback(
    (pickup: PipelinePickup) => {
      const nextStage = NEXT_STAGE[pickup.stage];
      if (!allowsManualStageChange(pickup.stage) || !nextStage || advancingUuid) {
        return;
      }

      setAdvancingUuid(pickup.uuid);

      api
        .post(`/logistics_tickets/${pickup.uuid}/advance`)
        .then(() => {
          alertSuccess(`Advanced ${pickup.displayId}`);
          onAdvanced?.();
        })
        .catch(() => {
          alertError(`Unable to advance ${pickup.displayId}. Please try again.`);
        })
        .finally(() => {
          setAdvancingUuid(null);
        });
    },
    [advancingUuid, alertError, alertSuccess, onAdvanced],
  );

  const hasResults = displayedPickups.length > 0;

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        boxSizing: "border-box",
      }}
    >
      {!hasResults ? (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            color: "text.secondary",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1">No pickups match your search.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            alignItems: "stretch",
            overflowX: "auto",
            overflowY: "hidden",
            pb: 1,
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: 4 },
          }}
        >
          {PIPELINE_COLUMNS.map((column) => (
            <PipelineColumn
              key={column.stage}
              column={column}
              pickups={pickupsByStage[column.stage] ?? []}
              onAction={advancePickup}
              advancingUuid={advancingUuid}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
